import Schema from 'async-validator'
import { i18n } from 'plugins/i18n.js'

function getValueByPatch(obj, path) {
  try {
    const keys = path.split('.')
    let value = obj
    for (const key of keys) {
      if (key.includes('[')) {
        const index = key.match(/\d+/)[0]
        const arrayKey = key.split('[')[0]
        value = value[arrayKey][index]
      } else {
        value = value[key]
      }
    }
    return value
  } catch (error) {
    return undefined
  }
}

const getEasyRule = (that, type, required, prefix, maxLen) => {
  return {
    type: type,
    required,
    trigger: 'change',
    validator: async (rule, value, callback, model) => {
      value = value || getValueByPatch(model, rule.field)
      if (!value) {
        callback(new Error(
          `${prefix}${i18n.t('Validate.Required')}`
        ))
      } else if (maxLen && value.length > maxLen) {
        callback(new Error(
          `${prefix}: ${i18n.t('Validate.maxLength', {number: maxLen, type: i18n.tc('Common.characters', maxLen) })}`
        ))
      } else {
        callback()
      }
    },
  }
}

export const rulesTemplate = {
  contactPerson: () => {
    return {
      name: {
        type: 'string',
        required: true,
        validator: (rule, value, callback, model) => {
          value = value || getValueByPatch(model, rule.field)
          if (!value) {
            callback(
              `${i18n.t('Common.Initials')}: ${i18n.t('Validate.Required')}`
            )
          } else {
            callback()
          }
        },
      },
      phone: {
        type: 'string',
        required: true,
        validator: (rule, value, callback) => {
          if (!value) {
            callback(
              `${i18n.t('Common.PhoneNumber')}: ${i18n.t('Validate.Required')}`
            )
          } else if (value != '+380 (––) ––– –– ––' && value?.includes('–')) {
            callback(
              `${i18n.t('Common.PhoneNumber')}: ${i18n.t('Tenders.PhoneNumRules')}`
            )
          } else {
            callback()
          }
        },
      },
      email: {
        type: 'string',
        min: 5,
        max: 50,
        trigger: 'change',
        transform(value) {
          return value?.trim()
        },
        validator: (rule, value, callback) => {
          if (!value) {
            callback()
          } else if (value.length < 5) {
            callback(
              `${i18n.t('Common.MailAddress')}: ${i18n.t('Validate.minLength', {number: 5, type: i18n.tc('Common.characters', 5) })}`
            )
          } else if (value.length > 50) {
            callback(
              `${i18n.t('Common.MailAddress')}: ${i18n.t('Validate.maxLength', {number: 50, type: i18n.tc('Common.characters', 50) })}`
            )
          } else if (!value.match(/^\s*([A-Za-z0-9_\-.'])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,10})\s*$/gm)) {
            callback(i18n.t('Tenders.MailRules', {mail: ''}))
          } else {
            callback()
          }
        },
      }
    }
  },
  lot: (that, index, propPrefix) => {
    const result = {}
    const keyPrefix = `${propPrefix ?? ''}lots[${index}]`
    const isGroupContext = propPrefix && propPrefix.includes('lotGroups')
    const lotLabel = isGroupContext ? i18n.t('Tenders.Position') : i18n.t('Tenders.Lot')

    result[`${keyPrefix}.lotSubject`] = getEasyRule(that, 'string', true, `${lotLabel} ${index + 1} ${i18n.t('Catalog.Position')}: `, 254)
    result[`${keyPrefix}.measuringUnit`] = getEasyRule(that, 'string', true, `${lotLabel} ${index + 1} ${i18n.t('Tenders.UnitOfMeasurement')}: `)
    result[`${keyPrefix}.quantity`] = getEasyRule(that, 'number', true, `${lotLabel} ${index + 1} ${i18n.t('Catalog.Amount')}: `)

    result[`${keyPrefix}.comment`] = {
      type: 'string',
      trigger: 'change',
      validator: (rule, value, callback, model) => {
        value = value || getValueByPatch(model, rule.field)
        if (value?.length > 4000) {
          callback(new Error(
            `${lotLabel} ${index + 1}: ${i18n.t('Tenders.AdditionalDescription')}: ${i18n.t('Validate.maxLength', {number: 4000, type: i18n.tc('Common.characters', 10) })}`
          ))
          return
        }
        callback()
      },
    }
    return result
  },
  lotGroup: (that, index) => {
    const result = {}
    const keyPrefix = index !== undefined ? `lotGroups[${index}].` : ''
    const labelPrefix = index !== undefined ? `${i18n.t('Tenders.Lot')} ${index + 1}: ` : ''

    result[`${keyPrefix}title`] = getEasyRule(that, 'string', true, `${labelPrefix}(${i18n.t('Common.Name')}) `, 254)
    result[`${keyPrefix}description`] = {
      type: 'string',
      trigger: 'change',
      validator: (rule, value, callback, model) => {
        value = value || getValueByPatch(model, rule.field)
        if (value?.length > 4000) {
          callback(new Error(
            `${labelPrefix}(${i18n.t('Common.Description')}): ${i18n.t('Validate.maxLength', {number: 4000, type: i18n.tc('Common.characters', 10) })}`
          ))
          return
        }
        callback()
      },
    }
    result[`${keyPrefix}lots`] = {
      type: 'array',
      min: 1,
      trigger: 'change',
      validator: (rule, value, callback, model) => {
        value = value || getValueByPatch(model, rule.field)
        if (value?.length === 0) {
          callback(new Error(
            `${labelPrefix} ${i18n.t('Validate.lotGroupMinOnePosition')}`
          ))
          return
        }
        if (!that.$can('use', 'allowLotDuplication')) {
          const hasDuplicates = value?.some((item, index) => {
            return value.findIndex((innerItem, innerIndex) => {
              return innerIndex !== index && innerItem.lotSubject && innerItem.measuringUnit && innerItem.lotSubject === item.lotSubject && innerItem.measuringUnit === item.measuringUnit
            }) !== -1
          })
          if (hasDuplicates) {
            callback(new Error(`${labelPrefix}: ${i18n.t('Validate.DublicatePositionNameAndUnit')}`))
            return
          }
        }
        callback()
      },
    }
    return result
  },
}

const dateToStr = (that, date, mode = 'dateTime') => {
  let result = that.$d(date, 'shortDate')
  if (mode === 'date') {
    return result
  }
  return `${result} ${that.$d(date, 'time')}`
}

const rules = {
  TenderInformation: (that) => {
    return {
      tenderSubject: [
        {
          type: 'string',
          required: true,
          trigger: 'change',
          transform(value) {
            return value?.trim()
          },
          validator: (rule, value, callback, model) => {
            value = value || getValueByPatch(model, rule.field)
            if (!value) {
              callback(new Error(`${i18n.t('Planning.Name')}: ${i18n.t('Validate.Required')}`))
            } else if (value?.length > 4000) {
              callback(new Error(`${i18n.t('Planning.Name')}: ${i18n.t('Validate.maxLength', {number: 4000, type: i18n.tc('Common.characters', 10) })}`))
            } else {
              callback()
            }
          }
        },
      ],
      'biddingType.biddingTypeId': getEasyRule(that, 'number', true, `${i18n.t('Automation.condition.tenderType')}: `),
      classificationCode: getEasyRule(that, 'string', true, `${i18n.t('Tenders.Category')}: `),
      tenderDescription: {
        type: 'string',
        max: 4000,
        message: i18n.t('Validate.maxLength', {number: 4000, type: i18n.tc('Common.characters', 10) })
      },
      tenderCurrency: getEasyRule(that, 'string', true, `${i18n.t('Automation.condition.tenderCurrency')}: `),
      contactPersonId: getEasyRule(that, 'number', true, `${i18n.t('Automation.condition.tenderContact')}: `),
      allowedCurrencies: {
        type: 'array',
        trigger: 'change',
        validator: (rule, value, callback, model) => {
          value = value || getValueByPatch(model, rule.field)
          if (!model?.currencyOwner || !model?.biddingType?.allowedCurrenciesSelectionByOrganizer) {
            callback()
          } else if (!value?.length) {
            callback(new Error(that.$t('Validate.AddAllowedCurrencies')))
          /*} else if (spr.currencies.some(c => !value.includes(c.code))) {
            callback(new Error(that.$t('Validate.WrongAllowedCurrencies'))) */
          } else {
            callback()
          }
        }
      },
      additionalContactPersons: {
        type: 'array',
        trigger: 'change',
        validator: (rule, value, callback, model) => {
          value = value || getValueByPatch(model, rule.field)
          if (!value?.length) {
            callback()
            return
          }
          const additionalContactPerson = value[0]
          const validator = new Schema(rulesTemplate.contactPerson(that))
          validator.validate(additionalContactPerson, (errors/*, fields*/) => {
            if (errors?.length) {
              callback(errors?.map(i => i.message))
            } else {
              callback()
            }
          })
        }
      },
      tenderPeriodTo: {
        type: 'string',
        required: true,
        trigger: 'change',
        validator: async (rule, value, callback, model) => {
          value = value || getValueByPatch(model, rule.field)
          if (model?.biddingType?.withoutPublication) {
            callback()
          } else if (!value) {
            callback(new Error(that.$t('Validate.NeedCheckAcceptingDate')))
          } else {
            const datePeriodTo = new Date(value)
            that.minProposalsDate =  await that.mixin_getMinProposalsDate()
            if (datePeriodTo < that.mixin_tenderDateMin.date) {
              const currDateStr = dateToStr(that, that.mixin_tenderDateMin.date)
              let textError = i18n.t('Validate.MinimalOfferDate', {date: currDateStr})
              callback(new Error(textError))
              return
            }
            const hoursPeriodTo = datePeriodTo.getHours()
            const minutesPeriodTo = datePeriodTo.getMinutes()

            const isAfterStartTime = hoursPeriodTo > 8 || (hoursPeriodTo === 8 && minutesPeriodTo >= 0)
            const isBeforeEndTime = hoursPeriodTo < 18 || (hoursPeriodTo === 18 && minutesPeriodTo === 0)

            if (!isAfterStartTime || !isBeforeEndTime) {
              callback(new Error(i18n.t('Validate.Time8_18')))
              return
            }
            const dayOfWeek = datePeriodTo.getDay()
            if (dayOfWeek === 0 || dayOfWeek === 6) {
              const getNextWorkingDay = (today) => {
                today = today || new Date()
                let dayOfWeek = today.getDay()

                do {
                  today.setDate(today.getDate() + 1)
                  dayOfWeek = today.getDay()
                } while (dayOfWeek === 0 || dayOfWeek === 6)
                return dateToStr(that, today, 'date')
              }
              callback(new Error(i18n.t('Validate.DateNotWeekDayNextWorkDay', {date: getNextWorkingDay(datePeriodTo)}) ))
              return
            }
            callback()
          }
        }
      },
      scheduledStart: {
        type: 'string',
        trigger: 'change',
        validator: (rule, value, callback) => {
          if (!value) {
            callback()
          } else if (new Date().getTime() > new Date(value).getTime()) {
            callback(new Error(i18n.t('Validate.WrongProposalsDate', { date: dateToStr(that, new Date()), for: i18n.t('Tenders.ForPublicationDate') })))
          } else {
            callback()
          }
        }
      },
      deliveryPeriodFrom: {
        type: 'string',
        trigger: 'change',
        validator: (rule, value, callback) => {
          if (!value) {
            callback()
            return
          }
          const tenderPeriodTo = that.tender?.tenderPeriodTo
          const deliveryPeriodTo = that.tender?.deliveryPeriodTo
          const deliveryPeriodFrom = that.tender?.deliveryPeriodFrom

          if (!deliveryPeriodFrom && !deliveryPeriodTo) {
            return
          }

          const checkDate = (min, date) => {
            min = min ? new Date(min) : null
            date = date ? new Date(date) : null
            if (min) {
              min.setHours(0, 0, 0, 0)
            }
            if (date) {
              date.setHours(0, 0, 0, 0)
            }
            if (date && new Date(min).getTime() > new Date(date).getTime()) {
              return dateToStr(that, new Date(min), 'date')
            }
            return null
          }
          const errorDateFrom = checkDate(tenderPeriodTo, deliveryPeriodFrom)
          const errorDateTo = checkDate(deliveryPeriodFrom || tenderPeriodTo, deliveryPeriodTo)

          if (!errorDateFrom && !errorDateTo) {
            callback()
            return
          }
          if (errorDateFrom) {
            callback(new Error(i18n.t('Validate.WrongProposalsDate', { date: errorDateFrom, for: i18n.t('Tenders.ForStartOfDelivery') })))
            return
          }
          if (deliveryPeriodTo && new Date(deliveryPeriodTo).getTime() < new Date(value).getTime()) {
            callback(new Error(i18n.t('Validate.StartDeliveryNeedLaterBeginer')))
            return
          }
          callback(new Error(i18n.t('Validate.WrongProposalsDate', { date: errorDateTo, for: i18n.t('Tenders.ForEndOfDelivery') })))
        }
      },
    }
  },
  TenderLots: (that) => {
    let result = {}
    const isLotGroup = (that.$can('show', 'useGroupLots') && that.tender?.biddingType?.groupedLotsEnabled) || !!that.tender?.lotGroups?.length
    if (isLotGroup) {
      let propName = ''
      that.tender?.lotGroups?.forEach((groupItem, groupIndex) => {
        result = { ...result, ...rulesTemplate.lotGroup(that, groupIndex)}

        propName = 'lotGroups[' + groupIndex + '].'
        groupItem?.lots?.forEach((lotItem, lotIndex) => {
          result = {...result, ...rulesTemplate.lot(that, lotIndex, propName)}
        })
      })
    } else {
      that.tender.lots?.forEach((lot, lotIndex) => {
        result = { ...result, ...rulesTemplate.lot(that, lotIndex)}
      })
    }
    result['lotGroupsGlobal'] = {
      trigger: 'change',
      type: 'array',
      validator: (rule, value, callback, model) => {
        if (!(that.$can('show', 'useGroupLots') && model?.biddingType?.groupedLotsEnabled)) {
          callback()
          return
        }
        value = model.lotGroups
        const allLots = value?.reduce((acc, group) => {
          if (group.lots?.length) {
            acc = [...acc, ...group.lots]
          }
          return acc
        }, []) ?? []
        const maximumLotsCountPerTender = that.settings?.tenderSettings?.maximumLotsCountPerTender
        if (allLots?.length > maximumLotsCountPerTender) {
          callback(new Error(`${i18n.t('Tenders.MaxPositionCount')} ${maximumLotsCountPerTender}`))
          return
        }
        if (!that.$can('use', 'allowLotDuplication')) {
          const hasDuplicates = allLots.some((item, index) => {
            return allLots.findIndex((innerItem, innerIndex) => {
              return innerIndex !== index && innerItem.lotSubject && innerItem.measuringUnit && innerItem.lotSubject === item.lotSubject && innerItem.measuringUnit === item.measuringUnit
            }) !== -1
          })
          if (hasDuplicates) {
            callback(new Error(i18n.t('Validate.DublicatePositionNameAndUnit')))
            return
          }
        }
        callback()
      }
    }
    return result
  },
  TenderCheck: (that) => {
    let result = {
      lots: {
        type: 'array',
        trigger: 'change',
        validator: (rule, value, callback, model) => {
          if (that.tender?.lotGroups?.length || (that.$can('show', 'useGroupLots') && that.tender?.biddingType?.groupedLotsEnabled)) {
            callback()
            return
          }
          value = value || getValueByPatch(model, rule.field)
          if (!value?.length) {
            callback(new Error(i18n.t('Validate.needMinOneLot')))
            return
          }
          const maximumLotsCountPerTender = that.settings?.tenderSettings?.maximumLotsCountPerTender
          if (value?.length > maximumLotsCountPerTender) {
            callback(new Error(`${i18n.t('Tenders.MaxLotCount')} ${maximumLotsCountPerTender}`))
            return
          }
          if (!that.$can('use', 'allowLotDuplication')) {
            const hasDuplicates = value?.some((item, index) => {
              return value.findIndex((innerItem, innerIndex) => {
                return innerIndex !== index && innerItem.lotSubject === item.lotSubject && innerItem.measuringUnit === item.measuringUnit
              }) !== -1
            })
            if (hasDuplicates) {
              callback(new Error(i18n.t('Validate.DublicateLotNameAndUnit')))
              return
            }
          }
          callback()
        }
      },
      lotGroups: {
        type: 'array',
        trigger: 'change',
        validator: (rule, value, callback, model) => {
          if (!(that.$can('show', 'useGroupLots') && that.tender?.biddingType?.groupedLotsEnabled)) {
            callback()
            return
          }
          value = value || getValueByPatch(model, rule.field)
          if (!value?.length) {
            callback(new Error(i18n.t('Validate.needMinOneLot')))
            return
          }
          callback()
        }
      },
      'members.invitedParticipants': {
        type: 'array',
        trigger: 'change',
        validator: (rule, value, callback, model) => {
          value = value || getValueByPatch(model, rule.field)
          if (model?.biddingType?.isPublic || model?.biddingType?.withoutPublication) {
            callback()
            return
          } else if (!model.membersCount && !value?.length) {
            callback(new Error(i18n.t('Tenders.AddSuppliers')))
            return
          } else {
            callback()
          }
        }

      }
    }
    return result
  },
}

export function getCombinedRules(thisModel, variables) {
  if (!variables?.length) {
    return {}
  }
  const combinedRules = {}

  variables.forEach((variable) => {
    const rule = rules[variable]

    if (rule) {
      Object.assign(combinedRules, rule(thisModel) || {})
    }
  })

  return combinedRules
}

function getAdditionalFieldProps(field) {
  let typeValidate = 'string'
  let maxLen
  switch (field.typeId) {
  case 'CHECKBOX' : {
    typeValidate = 'boolean'
    break
  }
  case 'DATE_TIME': {
    typeValidate = 'date'
    break
  }
  case 'TEXT': {
    typeValidate = 'string'
    maxLen = 4000
    break
  }
  case 'STRING': {
    typeValidate = 'string'
    maxLen = 254
    break
  }
  case 'INTEGER':
  case 'DECIMAL': {
    typeValidate = 'number'
    break
  }
  case 'URL': {
    typeValidate = 'url'
    break
  }
  case 'PHONE':
  case 'DROPDOWN': {
    typeValidate = 'string'
    break
  }
  case 'EMAIL': {
    typeValidate = 'email'
    maxLen = 254
    break
  }
  default: {
    typeValidate = 'string'
    break
  }
  }
  return {typeValidate, maxLen}
}

/**
 * правла для доп. полей тендера
 * @param {*} filed
 * @param {*} isExists
 */
export function getAdditionalFieldTenderRules(thisModel, field, isExists) {
  const rulesName = `${isExists ? 'existing_fields' : 'additional_fields'}.${field.id}`
  const required = field.settingValues?.some(set => set.settingType == 'REQUIRED' && eval(set.value))

  const {typeValidate, maxLen} = getAdditionalFieldProps(field)

  const validateProps = {
    this: thisModel,
    checkValue: field.value,
    required,
    typeValidate,
    type: field.typeId,
    maxLen,
    caption: field.caption,
  }
  return {[rulesName]: getAdditionalRules(validateProps)}
}
export function getAdditionalFieldLotsRules(thisModel, field) {
  let result = {}
  thisModel?.tender?.lots?.forEach((lot, lotIndex) => {
    result = {...result, ...getAdditionalFieldLotRules(thisModel, lotIndex, field)}
  })

  return result
}

export function getAdditionalFieldLotRules(thisModel, index, field, isGroupContext = false) {
  const {typeValidate, maxLen} = getAdditionalFieldProps(field)
  const rulesName = 'lots[' + index + '].additional_fields.' + field.id + '.value'
  const required = field.settingValues?.some(set => set.settingType == 'REQUIRED' && eval(set.value))
  const lotLabel = isGroupContext ? i18n.t('Tenders.Position') : i18n.t('Tenders.Lot')

  const validateProps = {
    this: thisModel,
    checkValue: field.value,
    required,
    typeValidate,
    type: field.typeId,
    maxLen,
    caption: `${lotLabel} ${index + 1} ${field.caption}`,
  }

  return {[rulesName]: getAdditionalRules(validateProps)}
}
export function getLotGroupRules(thisModel, index) {
  return rulesTemplate.lotGroup(thisModel, index)
}

export function getLotRules(thisModel, index, propPrefix) {
  return rulesTemplate.lot(thisModel, index, propPrefix)
}

function getAdditionalRules(validateProps) {
  return {
    type: validateProps.typeValidate,
    required: validateProps.required,
    trigger: 'change',
    validator: (rule, value, callback, model) => {
      value = value || validateProps.checkValue || getValueByPatch(model, rule.field)

      switch (validateProps.typeValidate) {
      case 'number' : {
        value = (value && typeof value === 'number')
          ? value
          : (value ? parseFloat(value) : null)
        break
      }
      case 'boolean' : {
        value = (typeof value === 'boolean')
          ? value || false
          : value === 'true'
        break
      }
      default: {
        value = value === 'string'
          ? value?.trim()
          : value
        break
      }
      }
      if (validateProps.required && !value && validateProps.typeValidate !== 'boolean') {
        callback(new Error(
          `${validateProps.caption ? validateProps.caption + ': ' : ''}${validateProps.this.$t('Validate.Required')}`
        ))
      } else if (value && validateProps.typeValidate === 'string' && validateProps.maxLen && value.length > validateProps.maxLen) {
        callback(new Error(
          `${validateProps.caption ? validateProps.caption + ': ' : ''}${validateProps.this.$t('Validate.maxLength', {number: validateProps.maxLen, type: validateProps.this.$tc('Common.characters', 10) })}`
        ))
      } else if (value && validateProps.typeValidate !== 'string' && validateProps.maxLen && value > validateProps.maxLen) {
        callback(new Error(
          `${validateProps.caption ? validateProps.caption + ': ' : ''}${validateProps.this.$t('Validate.maxLength', {number: validateProps.maxLen, type: validateProps.this.$tc('Common.characters', 10) })}`
        ))
      } else if (value && validateProps.type === 'PHONE' && !value?.match(/^\+\d{12}$/)) {
        callback(
          `${validateProps.caption ? validateProps.caption + ': ' : ''}${new Error(validateProps.this.$t('Tenders.PhoneNumRules'))}`
        )
      } else if (value && validateProps.type === 'EMAIL' && !value?.match(/^\s*([A-Za-z0-9_\-.'])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,10})\s*$/)) {
        callback(new Error(
          `${validateProps.caption ? validateProps.caption + ': ' : ''}${validateProps.this.$t('Tenders.MailRules', {mail: ''})}`)
        )
      } else if (value && validateProps.type === 'URL' && !value?.match(/^(http|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:\\/~+#-]*[\w@?^=%&amp;\\/~+#-])?$/)) {
        callback(new Error(
          `${validateProps.caption ? validateProps.caption + ': ' : ''}${validateProps.this.$t('Validate.LinkRules')}`)
        )
      } else {
        callback()
      }
    },
  }
}
